import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BACKEND_URL } from './backendConfig';

/**
 * ASSIGNING A DESIGNER AND A DEVELOPER PER SKU.
 *
 * Jay, 2026-10-01, point 2b: "When he clicks it, he should see the list of SKUs in that project.
 * Each SKU has two dropdowns — Designer and Developer. He picks them, hits Save, and those names
 * are written into the Sample IDs that were created in step 1."
 *
 * ONE COMPONENT, TWO DOORS. `api` is the only thing that differs: the receiving team arrives on the
 * ticket link, which is unauthenticated by design (the ticket goes to a group mailbox in Korea and
 * whoever opens it may hold no portal login — Max, 2026-10-04); the two managers arrive from the
 * approval notice with a session. A second copy of this screen is how one door comes to write what
 * the other refuses.
 *
 * THE DROPDOWN IS PER ROUND, NOT PER SKU. A SKU can carry several sample rounds, and a control that
 * said "the SKU" would have to pick one of them to write to — the kind of choice that quietly lands
 * on the wrong row. The rounds are drawn out, each with its own Sample ID beside it, so the person
 * assigning can see exactly what they are putting a name on.
 *
 * A BLANK CLEARS. Taking a name off is as real an edit as putting one on, and a page whose only
 * possible change is to add has no way to correct a mistake.
 */
export default function AssignSamplePeople({ api, title, by }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});   // sampleRoundId -> { designerId, developerId }
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(0);

  const load = useCallback(async () => {
    setErr('');
    try {
      const res = await fetch(`${BACKEND_URL}${api}`, { credentials: 'include' });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || t('xAssign.failed', { status: res.status }));
      setData(d);
      setDraft({});
    } catch (e) { setErr(e.message); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);
  useEffect(() => { load(); }, [load]);

  /* The box reads from the draft with the stored value behind it, so a reload does not have to
     rebuild the draft and an untouched row cannot be saved back over itself with a stale value. */
  const valOf = (r, k) => {
    const d = draft[r.sampleRoundId];
    const v = d && d[k] !== undefined ? d[k] : r[k];
    return v === null || v === undefined ? '' : String(v);
  };
  const set = (r, k, v) => setDraft((p) => ({
    ...p, [r.sampleRoundId]: { ...(p[r.sampleRoundId] || {}), [k]: v === '' ? null : Number(v) },
  }));
  const dirtyCount = Object.keys(draft).length;

  const save = async () => {
    setSaving(true); setErr('');
    try {
      const entries = Object.entries(draft).map(([id, v]) => {
        const r = (data.skus || []).flatMap((s) => s.rounds).find((x) => String(x.sampleRoundId) === id) || {};
        return {
          sampleRoundId: Number(id),
          designerId: v.designerId !== undefined ? v.designerId : (r.designerId ?? null),
          developerId: v.developerId !== undefined ? v.developerId : (r.developerId ?? null),
        };
      });
      const res = await fetch(`${BACKEND_URL}${api}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entries, by: by || '' }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || t('xAssign.failed', { status: res.status }));
      setSaved(d.saved || 0);
      // The tick clears itself; one that stays on stops meaning "that just went".
      setTimeout(() => setSaved(0), 4000);
      await load();
    } catch (e) { setErr(e.message); }
    setSaving(false);
  };

  if (err && !data) return <div style={S.err}>{err}</div>;
  if (!data) return <div style={S.muted}>{t('xAssign.loading')}</div>;

  /* TWO LISTS, NOT ONE. The Designer box offered all 36 active employees — including Max himself
     — until 2026-10-04; the Sample Tracker's own pickers have always been built from who has
     actually done the work, and this screen writes into the same two columns. */
  const developers = data.developers || [];
  const designers = data.designers || [];
  /* A NAME ALREADY ON THE ROUND IS KEPT IN ITS OWN BOX even when it is not on the list — somebody
     who has left, or a one-off. A dropdown that silently drops the stored value writes a blank
     over it on the next save, which is how an assignment disappears without anybody touching it. */
  const withCurrent = (list, id) => (
    id && !list.some((p) => String(p.id) === String(id))
      ? [...list, { id, name: t('xAssign.notOnList', { name: nameById(id) || id }) }] : list);
  const nameById = (id) => {
    const hit = [...developers, ...designers].find((p) => String(p.id) === String(id));
    return hit ? hit.name : '';
  };
  const rows = (data.skus || []).filter((s) => s.rounds.length);
  /* A SKU WITH NO SAMPLE IS SHOWN, NOT HIDDEN. Samples are created when the SKU is approved, so one
     with none is either older than that change or something went wrong when it was minted — and a
     row that silently vanishes from this list is how nobody notices either. */
  const noSample = (data.skus || []).filter((s) => !s.rounds.length);

  return (
    <div>
      <div style={S.h}>{title || t('xAssign.title')}</div>
      <div style={S.sub}>
        {data.groupId} — {data.groupName}
      </div>
      <p style={S.note}>{t('xAssign.note')}</p>
      {err && <div style={S.err}>{err}</div>}

      {rows.length === 0 && <p style={S.muted}>{t('xAssign.noSample')}</p>}

      {rows.map((s) => (
        <div key={s.projectId} style={S.card}>
          <div style={S.cardH}>
            <strong>{s.itemName || '—'}</strong>
            <span style={S.sku}>{s.projectId}</span>
          </div>
          {s.rounds.map((r) => (
            <div key={r.sampleRoundId} style={S.round}>
              <div style={S.sampleId}>{r.sampleId}</div>
              <label style={S.field}>
                <span style={S.label}>{t('xAssign.designer')}</span>
                <select style={S.select} value={valOf(r, 'designerId')}
                  onChange={(e) => set(r, 'designerId', e.target.value)}>
                  <option value="">—</option>
                  {withCurrent(designers, r.designerId).map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>))}
                </select>
              </label>
              <label style={S.field}>
                <span style={S.label}>{t('xAssign.developer')}</span>
                <select style={S.select} value={valOf(r, 'developerId')}
                  onChange={(e) => set(r, 'developerId', e.target.value)}>
                  <option value="">—</option>
                  {withCurrent(developers, r.developerId).map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>))}
                </select>
              </label>
            </div>
          ))}
        </div>
      ))}

      {noSample.length > 0 && (
        <p style={S.muted}>
          {t('xAssign.noSampleOn', { list: noSample.map((s) => s.projectId).join(', ') })}
        </p>
      )}

      {rows.length > 0 && (
        <div style={S.footer}>
          {/* Three states, the same as every other save in this codebase: quiet with nothing to
              save, live when there is, a tick when it went. */}
          {saved > 0
            ? <span style={S.ok}>{t('xAssign.savedN', { n: saved })}</span>
            : (
              <button type="button" onClick={save} disabled={!dirtyCount || saving} style={dirtyCount ? S.btnOn : S.btn}>
                {saving ? t('xAssign.saving') : dirtyCount ? t('xAssign.saveN', { n: dirtyCount }) : t('xAssign.save')}
              </button>
            )}
        </div>
      )}
    </div>
  );
}

const S = {
  h: { fontSize: 16, fontWeight: 700, color: '#111827' },
  sub: { fontSize: 13, color: '#6B7280', marginTop: 2 },
  note: { fontSize: 12, color: '#6B7280', margin: '10px 0 14px', lineHeight: 1.5 },
  muted: { fontSize: 13, color: '#6B7280', margin: '10px 0' },
  err: { background: '#FEF2F2', border: '1px solid #FECACA', color: '#B91C1C',
    borderRadius: 8, padding: '9px 12px', fontSize: 13, margin: '10px 0' },
  card: { border: '1px solid #E5E7EB', borderRadius: 10, padding: 12, marginBottom: 10, background: '#fff' },
  cardH: { display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8, fontSize: 14, color: '#111827' },
  sku: { fontSize: 12, color: '#6B7280' },
  /* A GRID, NOT A WRAPPING FLEX ROW. As flex the three parts broke apart at the ticket page's own
     width — the Sample ID and Designer on one line, Developer alone on the next — so the two boxes
     that belong to one round read as unrelated controls. Seen only by screenshotting it. The id
     spans the row above its own two dropdowns, which keeps them side by side at every width this
     page is read at, and the whole block collapses to one column on a phone. */
  round: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: 10, alignItems: 'end', padding: '10px 0', borderTop: '1px dashed #E5E7EB' },
  sampleId: { gridColumn: '1 / -1', fontSize: 12, color: '#374151', fontWeight: 600 },
  field: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 },
  label: { fontSize: 11, color: '#6B7280', fontWeight: 600 },
  select: { padding: '7px 10px', border: '1.5px solid #D1D5DB', borderRadius: 8, fontSize: 13,
    background: '#fff', width: '100%', boxSizing: 'border-box' },
  footer: { display: 'flex', justifyContent: 'flex-end', marginTop: 4 },
  btn: { padding: '9px 16px', borderRadius: 8, border: '1px solid #D1D5DB', background: '#fff',
    color: '#9CA3AF', fontSize: 14, fontWeight: 600, cursor: 'not-allowed' },
  btnOn: { padding: '9px 16px', borderRadius: 8, border: 'none', background: '#2563EB',
    color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  ok: { color: '#16A34A', fontSize: 14, fontWeight: 600 },
};
